'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logWebHeaderHovered from '@alltrails/amplitude/events/logWebHeaderHovered';
import logWebHeaderLoginButtonTapped from '@alltrails/amplitude/events/logWebHeaderLoginButtonTapped';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getFullName from '@alltrails/core/utils/getFullName';
import Avatar from '@alltrails/denali/components/Avatar';
import Button from '@alltrails/denali/components/Button';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import QrCode from '@alltrails/denali/icons/QrCode';
import { DropdownMenuOption } from '@alltrails/denali/types';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import InstallModal from '@alltrails/install-prompt/components/InstallModal';
import CreateRouteButton from './CreateRouteButton';
import GetTheAppButton from './GetTheAppButton';
import { getHeaderLinkOnClickWithAnalytics, getUserLinks, getUserPermissionLinks, HeaderLink } from './headerLinks';
import StartFreeTrialButton from './StartFreeTrialButton';
import styles from './styles/styles.module.scss';
import { HeaderBackground } from './types';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const GenericUpsellModal = dynamic(() => import('@alltrails/upsell/components/UpsellSystem/GenericUpsell/GenericUpsellModal'), { ssr: false });

type UserCTAProps = {
  background: HeaderBackground;
};

const UserCTA = ({ background }: UserCTAProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const experiments = useExperiments();

  const isGlobalNavCtaEligible = Boolean(user?.isPromoEligible);
  const globalNavCtaVariant = experiments['web-growth-rm-desktop-global-nav-cta']?.value;
  const sendGlobalNavCtaExposure = useExposureEvent('web-growth-rm-desktop-global-nav-cta');

  const [isOpen, setIsOpen] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);

  useEffect(() => {
    if (isGlobalNavCtaEligible) {
      sendGlobalNavCtaExposure();
    }
  }, [isGlobalNavCtaEligible, sendGlobalNavCtaExposure]);

  const navCreateXPValue = experiments['web-disco-rm-nav-create']?.value ?? 'control';
  const createRouteButton = useMemo(() => {
    if (navCreateXPValue === 'button-secondary' || navCreateXPValue === 'button-tertiary') {
      const variant = navCreateXPValue === 'button-secondary' ? 'secondary' : 'tertiary';
      if (background === 'transparent-inverted') {
        return (
          <div data-at-theme-colors="dark">
            <CreateRouteButton variant={variant} />
          </div>
        );
      }
      return <CreateRouteButton variant={variant} />;
    }
    return null;
  }, [background, navCreateXPValue]);

  if (!user) {
    return (
      <div className={styles.userCta}>
        {createRouteButton}
        <GetTheAppButton variant="accent" />
        <Button
          onClick={() => {
            logWebHeaderLoginButtonTapped();
            setIsSignUpModalOpen(true);
          }}
          size="sm"
          text={<FormattedMessage defaultMessage="Log in" />}
          variant={background === 'transparent-inverted' ? 'primary' : 'primary'}
        />
        {isSignUpModalOpen && (
          <SignUpModal
            autoClose
            closeModal={() => setIsSignUpModalOpen(false)}
            isOpen={isSignUpModalOpen}
            onSuccess={() => {
              setIsSignUpModalOpen(false);
              window.location.reload();
            }}
            trigger="Global Nav Menu"
            upsellTrigger="Global Nav Menu"
          />
        )}
      </div>
    );
  }

  const openDropdown = () => {
    logWebHeaderHovered({ web_header_position: navCreateXPValue === 'dropdown' ? 4 : 3, web_header_section: 'my account' });
    setIsOpen(true);
  };

  // Wrapping div around the Avatar is needed for the DropdownMenu to set a ref
  const anchor = (
    <div>
      <Avatar onClick={openDropdown} size="sm" userId={user.id} userName={getFullName(user)} />
    </div>
  );

  const options: DropdownMenuOption[] = getUserLinks({ user, languageRegionCode });
  if (isSubscriber(user)) {
    options.splice(
      1,
      0,
      { sectionHeader: null },
      {
        onClick: () => {
          setIsOpen(false);
          setShowInstallModal(true);
        },
        title: <FormattedMessage defaultMessage="Continue in app" />,
        trailingIcon: { icon: { Component: QrCode, size: 'sm' } }
      },
      { sectionHeader: null }
    );
  }
  options.push(...getUserPermissionLinks(user, languageRegionCode));

  const optionsWithAnalytics = options.map((option: HeaderLink | DropdownMenuOption, index) => {
    if ('webHeaderDropDownLink' in option) {
      const onClick = getHeaderLinkOnClickWithAnalytics(index, option.webHeaderDropDownLink, 'my account', option.onClick);
      return { ...option, onClick };
    }
    return option;
  });

  return (
    <div className={styles.userCta}>
      {createRouteButton}
      {isGlobalNavCtaEligible && globalNavCtaVariant === 'treatment-a' ? (
        <>
          <StartFreeTrialButton onClick={() => setIsUpsellOpen(true)} {...(background === 'transparent-inverted' && { variant: 'inverse' })} />
          <GetTheAppButton variant="accent" />
        </>
      ) : isGlobalNavCtaEligible && globalNavCtaVariant === 'treatment-b' ? (
        <>
          <GetTheAppButton {...(background === 'transparent-inverted' && { variant: 'inverse' })} />
          <StartFreeTrialButton onClick={() => setIsUpsellOpen(true)} variant="accent" />
        </>
      ) : (
        <GetTheAppButton variant="accent" />
      )}
      <DropdownMenu
        anchor={anchor}
        placement="bottom-end"
        className={styles.dropdownMenu}
        containerMouseHandlers={{ onMouseEnter: openDropdown, onMouseLeave: () => setIsOpen(false) }}
        isOpen={isOpen}
        onCloseRequest={() => setIsOpen(false)}
        options={optionsWithAnalytics}
      />
      <InstallModal isOpen={showInstallModal} onCloseRequest={() => setShowInstallModal(false)} location="global nav profile dropdown" />
      {isUpsellOpen && (
        <GenericUpsellModal
          heading={<FormattedMessage defaultMessage="Elevate your adventure" />}
          isOpen={isUpsellOpen}
          onCloseRequest={() => setIsUpsellOpen(false)}
          promoEligibleHeading={<FormattedMessage defaultMessage="Your first week’s on us" />}
          upsellTrigger="Global Nav Menu"
        />
      )}
    </div>
  );
};

export default UserCTA;
